import styled, { keyframes, css } from "styled-components";
import ethIcon from "../img/eth-icon.png";
import { useEffect, useState } from "react";
import SlotCounter from "react-slot-counter";
import { Typography } from "@bracket/ui";

const ChartTopBarWrapper = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  width: 100%;
  z-index: 1;
  flex-wrap: wrap;
  gap: 2.5rem;

  @media (max-width: 1620px) {
    gap: 2rem;
  }
`;

const Box = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: flex-start;
  height: auto;
`;

const EthBox = styled(Box)`
  flex-flow: row;
  justify-content: center;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 260px;

  @media (max-width: 1500px) {
    min-width: 210px;
  }
`;

const ChooseSideBox = styled(Box)`
  justify-content: center;
  align-items: flex-start;
  word-wrap: nowrap;
  margin-right: 10px;

  @media (max-width: 1500px) {
    justify-content: flex-end;
    align-items: flex-end;
    flex-basis: 100%;
    margin-bottom: 1rem;
    margin-right: 2rem;
  }
`;

const timerBlinkAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
    filter: brightness(1);
  }
  30% {
    transform: scale(1.1);
    filter: brightness(0.9);
  }
`;

const timerBlinkAnimationRule = css`
  ${timerBlinkAnimation} 0.6s ease-in-out infinite
`;

const Timer = styled.div<{ $isActive: boolean; $blink: boolean }>`
  display: flex;
  flex-flow: row;
  justify-content: center;
  align-items: center;
  border: 1px solid gray;
  color: gray;
  border-radius: 0.5rem;
  padding: 0.5rem;
  min-width: 115px;
  width: 115px;
  opacity: 0.2;

  ${({ $isActive }) =>
    $isActive &&
    css`
      border: 2px solid var(--primary);
      opacity: 0.8;
    `}

  ${({ $blink }) =>
    $blink &&
    css`
      animation: ${timerBlinkAnimationRule};
    `}
`;

const StyledTypography = styled(Typography)`
  @media (max-width: 1620px) {
    font-size: ${(props) =>
      props.variant === "h6" ? "1.1rem" : "2rem"} !important;
  }
`;

type ChartTopBarProps = {
  ethPrice: number | string;
  barsWonToday: number;
  maxBarsPerDay: number;
  time: number;
  isGameActive: boolean;
};

function formatTime(seconds: number): string {
  const formattedSeconds = String(seconds).padStart(2, "0");
  return `00:${formattedSeconds}`;
}

export const ChartTopBar = ({
  ethPrice,
  barsWonToday,
  maxBarsPerDay,
  time,
  isGameActive,
}: ChartTopBarProps) => {
  const formattedTime = formatTime(time);
  const [blinkClass, setBlinkClass] = useState(false);

  useEffect(() => {
    if (time < 10) {
      setBlinkClass(true);

      const timeoutId = setTimeout(() => {
        setBlinkClass(false);
      }, 600);

      return () => clearTimeout(timeoutId);
    }
  }, [time]);

  return (
    <ChartTopBarWrapper>
      <EthBox>
        <img src={ethIcon} width="35px" alt="ETH icon" />
        <Box>
          <StyledTypography variant="h6">ETH price</StyledTypography>
          <StyledTypography variant="h3" color="var(--primary)">
            ${ethPrice}
          </StyledTypography>
        </Box>
      </EthBox>

      <Box>
        <StyledTypography variant="h6">[BARS] won today</StyledTypography>
        <StyledTypography variant="h3" color="var(--primary)">
          <SlotCounter value={barsWonToday} animateUnchanged />
        </StyledTypography>
      </Box>
      <Box>
        <StyledTypography variant="h6">[BARS] per play</StyledTypography>
        <StyledTypography variant="h3">100</StyledTypography>
      </Box>
      <Box>
        <StyledTypography variant="h6">Max [BARS] per day</StyledTypography>
        <StyledTypography variant="h3">{maxBarsPerDay}</StyledTypography>
      </Box>
      <ChooseSideBox>
        <StyledTypography variant="h6">Countdown</StyledTypography>
        <Timer $isActive={isGameActive} $blink={blinkClass}>
          <Typography
            variant="h4"
            as="div"
            color={isGameActive ? "var(--primary)" : "gray"}
          >
            {formattedTime}
          </Typography>
        </Timer>
      </ChooseSideBox>
    </ChartTopBarWrapper>
  );
};
